import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Users, Search, ChevronLeft, ChevronRight, ShieldAlert, Lock, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/services/api';

const ROLES = ['user', 'dj', 'organizer', 'admin'];

function Centered({ children }) {
  return <div className="max-w-4xl mx-auto px-4 py-16 text-center opacity-70">{children}</div>;
}
function GateLogin() {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <Lock className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <p className="mb-4">Please log in.</p>
      <Link to="/login"><Button>Go to Login</Button></Link>
    </div>
  );
}
function GateAdmin({ role }) {
  return (
    <div className="max-w-xl mx-auto px-4 py-16 text-center">
      <ShieldAlert className="mx-auto mb-4 h-10 w-10 opacity-60" />
      <h1 className="text-xl font-semibold mb-2">Admin access required</h1>
      <p className="opacity-80">Your role is <span className="capitalize font-medium">{role || 'unknown'}</span>.</p>
    </div>
  );
}

export default function AdminUsers() {
  const [authed] = useState(api.isAuthenticated());
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ users: [], page: 1, limit: 20, total: 0, pages: 0 });
  const [page, setPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ page: String(page), limit: '20' });
      if (roleFilter) qs.set('role', roleFilter);
      if (query) qs.set('search', query);
      const res = await api.get(`/admin/users?${qs.toString()}`);
      setData(res);
    } catch (e) {
      toast.error(e.message || 'Failed to load users');
    }
  }, [page, roleFilter, query]);

  useEffect(() => {
    if (!authed) { setLoading(false); return; }
    api.get('/users/me').then((me) => setRole(me.role)).catch(() => {}).finally(() => setLoading(false));
  }, [authed]);

  useEffect(() => {
    if (role === 'admin') fetchUsers();
  }, [role, fetchUsers]);

  const applySearch = (e) => { e.preventDefault(); setPage(1); setQuery(search.trim()); };

  const viewDetail = async (uid) => {
    try { setSelected(await api.get(`/admin/users/${uid}`)); }
    catch (e) { toast.error(e.message || 'Failed to load detail'); }
  };

  const updateRole = async (uid, newRole, currentRole) => {
    if (newRole === currentRole) return;
    if (!window.confirm(`Change role to "${newRole}"?`)) return;
    try {
      await api.patch(`/admin/users/${uid}/role`, { role: newRole });
      toast.success('Role updated');
      fetchUsers();
      if (selected?.user_id === uid) viewDetail(uid);
    } catch (e) {
      toast.error(e.message || 'Role update failed');
    }
  };

  if (!authed) return <GateLogin />;
  if (loading) return <Centered>Loading…</Centered>;
  if (role !== 'admin') return <GateAdmin role={role} />;

  const totalPages = data.pages || 1;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-4"><Users className="h-6 w-6" /> Admin · Users</h1>

      <form onSubmit={applySearch} className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="search">Search</Label>
          <Input id="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="email, name, username, city" />
        </div>
        <div>
          <Label htmlFor="rf">Role</Label>
          <select id="rf" value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm">
            <option value="">All</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <Button type="submit"><Search className="h-4 w-4 mr-2" /> Search</Button>
      </form>

      {selected && (
        <Card className="mb-4 border-primary/40">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">User detail</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <div><b>{selected.full_name || selected.name}</b> · {selected.email}</div>
            <div>username: {selected.username || '—'} · role: <span className="capitalize">{selected.role}</span> · {selected.city || '—'} {selected.state || ''}</div>
            <div>email_verified: {String(selected.email_verified)} · phone_verified: {String(selected.phone_verified)}</div>
            <div className="opacity-60">user_id: {selected.user_id}</div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {data.users.length === 0 && <p className="opacity-60 text-center py-8">No users found.</p>}
        {data.users.map((u) => (
          <Card key={u.user_id}>
            <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{u.full_name || u.name || '(no name)'} <span className="opacity-60 text-sm">{u.email}</span></div>
                <div className="text-sm opacity-70">@{u.username || '—'} · {u.city || '—'} {u.state || ''}</div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="capitalize">{u.role}</Badge>
                <select value={u.role} onChange={(e) => updateRole(u.user_id, e.target.value, u.role)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <Button variant="outline" size="sm" onClick={() => viewDetail(u.user_id)}><Eye className="h-4 w-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="text-sm opacity-70">Page {data.page} of {totalPages} · {data.total} users</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={data.page >= totalPages} onClick={() => setPage((p) => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
